import {
  ALL_MODULES,
  getTranslatedText,
  ENTITY_MAP,
  COMMON_CONSTANTS
} from '@nile/components';
import {
  AccountClient,
  getApiMethodAndKeyNames,
  apiResolver,
  GET_FIELD,
  GET_REQUESTERS_BY_CRITERIA,
  GET_TECHNICIANS_BY_CRITERIA,
  TicketClient,
  GET_CLIENT_USERS
} from '@nile/graphql';
import { includes } from 'lodash';

function getQueryVars(item) {
  const data = {
    query: GET_CLIENT_USERS,
    dataLevel: 'getClientUserAssociationsList.associations'
  };
  switch (item) {
    case 'name':
      return {
        ...data,
        valueLevel: 'user.name'
      };

    case 'email':
      return {
        ...data,
        valueLevel: 'user.email'
      };
  }
}

/**
 * For entity type, if we want to call a specif api, this method will be called.
 * @param {object} item
 */
function getQuery(item) {
  const entity = item?.name?.split('.')[0] || '';
  switch (entity) {
    case 'technician':
    case 'followers':
      return GET_TECHNICIANS_BY_CRITERIA;
    default:
      return GET_REQUESTERS_BY_CRITERIA;
  }
}

/**
 * After getting values from the API, this value specifies
 * the method name to process the data
 * @param {object} item
 */
function getNameKey(item) {
  const entity = item?.split('.')[0] || '';
  switch (entity) {
    case 'technician':
    case 'followers':
      return 'getTechnicianList';
    default:
      return 'getRequesterList';
  }
}

function formatOperatorValues(allOperators, operator, intl) {
  return allOperators.getRuleBuilderOperators.operator_placeholders[
    operator
  ].map((el) => {
    return {
      ...el,
      label: getTranslatedText(intl, el.key),
      value: getTranslatedText(intl, el.key),
      name: el.key
    };
  });
}

/**
 * This method gives the query and client using which the API calls will be made
 * after selecting the attribute
 * @param {object} item
 * @param {String} operator
 */

function getPropValues(item, operator, allOperators, intl, clientId) {
  // site.name
  if (!item?.name) return {};
  if (item.isCustom) {
    if (includes(['date', 'datetime'], item.dataType) && operator) {
      return formatOperatorValues(allOperators, operator, intl);
    }
    let module = 'requester';
    let columnName = item?.name || item?.columnName;
    if (item.name.split('.')?.length > 1) {
      module = item.name.split('.').shift();
      columnName = item.name.split('.').pop();
    }
    return AccountClient.query({
      query: GET_FIELD,
      variables: {
        input: {
          columnName,
          module: ALL_MODULES[module] || ALL_MODULES.requester
        }
      }
    });
  }
  const query = apiResolver.Requester[item.name]?.api;
  const client = apiResolver.Requester[item.name]?.client;
  let variables = apiResolver.Requester[item.name]?.variables || {};

  variables = {
    input: {
      sort: [
        {
          attribute: 'name',
          order: 'ASC'
        }
      ],
      ...(clientId && {
        condition: {
          joinOperator: 'AND',
          operands: [
            {
              value: clientId,
              operator: 'is',
              attribute: 'account.accountId'
            }
          ]
        }
      })
    }
  };
  if (query && client) {
    return client.query({
      query: query,
      variables: variables
    });
  }
  // if (operator) {
  //   return formatOperatorValues(allOperators, operator, intl);
  // }
  // return { client: AccountClient, query: getQuery(item) };
}

async function onAttributeSelect(params, clientId) {
  const { item: field, operator, fields, allOperators, intl } = params;
  const allFields = fields;
  const selectedField = allFields.find((el) => el.name === field);

  if (selectedField?.dataType === 'boolean') {
    return [
      {
        name: 'true',
        label: 'true',
        value: true
      },
      {
        name: 'false',
        label: 'false',
        value: false
      }
    ];
  }
  const allValues = await getPropValues(
    selectedField,
    operator,
    allOperators,
    intl,
    clientId
  );
  let res = [];

  if (selectedField?.name) {
    const apiMethodAndKeyNames = getApiMethodAndKeyNames(ENTITY_MAP.requester);
    let method = apiMethodAndKeyNames[selectedField.name]?.method;
    const key = apiMethodAndKeyNames[selectedField.name]?.key;
    const secondaryMethod =
      apiMethodAndKeyNames[selectedField.name]?.secondaryMethod;
    const nestedKey = apiMethodAndKeyNames[selectedField.name]?.nestedKey;
    let dataToMap = null;
    if (secondaryMethod) {
      dataToMap = allValues.data[method]?.[secondaryMethod];
    } else if (operator === 'on') {
      dataToMap = allValues;
    } else {
      dataToMap = allValues.data?.[method];
    }
    if (selectedField.nestedTo) {
      let nestedProp = apiMethodAndKeyNames[selectedField.name]?.nestedKey;
      if (!dataToMap && !method) {
        dataToMap = allValues.data?.getField?.options;
      }
      dataToMap.forEach((el) => {
        if (el[nestedProp]) {
          res = res.concat(
            el[nestedProp].map((c) => {
              return {
                name: c[key],
                value: c[key],
                label: c[key]
              };
            })
          );
        } else {
          res = res.concat([
            { name: el?.value, value: el?.value, label: el?.value }
          ]);
        }
      });
    } else if (operator === 'on') {
      res = allValues;
    } else if (selectedField.isCustom) {
      method = 'getField';
      const fldOptions =
        allValues?.data?.[method]?.options ||
        allValues?.data?.[method]?.fieldOptions;
      res = allValues?.data
        ? fldOptions?.map((el) => {
            return {
              name: el?.value,
              value: el?.value,
              label: el?.value
            };
          })
        : [];
    } else if (nestedKey) {
      res = dataToMap?.[nestedKey]
        ? dataToMap?.[nestedKey].map((el) => {
            return {
              name: el[key],
              value: el[key],
              label: el[key]
            };
          })
        : [];
    } else {
      res = dataToMap
        ? dataToMap.map((el) => {
            return {
              name: el[key],
              value: el[key],
              label: el[key]
            };
          })
        : [];
    }
  }
  return res;
}

/**
 * This method is called for autocomplete fields.
 * Usually apis will be called when we choose attributes. But for autocomplete,
 * on typing few characters, api will be called. This is the method.
 * @param {object} client
 * @param {gql} query
 * @param {object} params
 */
const onValueSearch = async (_, __, params, clientId) => {
  // name, email
  const { query, dataLevel, valueLevel } = getQueryVars(params.field);
  const variables = {
    listInfo: {
      condition: {
        joinOperator: 'AND',
        operands: [
          {
            attribute: valueLevel,
            operator: 'contains',
            value: params.inputValue
          }
        ]
      }
    },
    ...(clientId && { clientId })
  };
  const res = await AccountClient.query({
    query: query,
    variables: { input: variables }
  });
  const value = getNestedValue(dataLevel, res?.data);
  const allOptions = value
    ? value.map((el) => {
        const itemVal = getNestedValue(valueLevel, el);
        return {
          name: itemVal,
          value: itemVal,
          label: itemVal
        };
      })
    : [];
  return allOptions;
};

function getNestedValue(key, data) {
  if (!data) return null;
  if (!key.includes('.')) return data;

  return key.split('.').reduce((acc, curr) => {
    if (!acc) return {};
    return acc[curr];
  }, data);
}

export { onAttributeSelect, onValueSearch };
